import { useEffect, useMemo, useRef, useState } from 'react';

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function AvatarCropper({
  file,
  onCancel,
  onConfirm,
  busy,
}: {
  file: File;
  onCancel: () => void;
  onConfirm: (crop: CropRect) => void;
  busy?: boolean;
}) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(false);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const preview = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => {
    const image = new Image();
    image.onload = () => {
      imageRef.current = image;
      setNatural({ width: image.naturalWidth, height: image.naturalHeight });
      setOffset({ x: 0, y: 0 });
      setReady(true);
    };
    image.src = preview;
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  const box = 280;
  const scale = natural.width && natural.height ? Math.max(box / natural.width, box / natural.height) : 1;
  const drawWidth = natural.width * scale;
  const drawHeight = natural.height * scale;
  const minX = Math.min(0, box - drawWidth);
  const minY = Math.min(0, box - drawHeight);
  const clamped = {
    x: Math.min(0, Math.max(minX, offset.x)),
    y: Math.min(0, Math.max(minY, offset.y)),
  };

  const crop = (): CropRect => ({
    x: Math.round(-clamped.x / scale),
    y: Math.round(-clamped.y / scale),
    width: Math.round(box / scale),
    height: Math.round(box / scale),
  });

  return (
    <div className="secret-modal" role="dialog" aria-modal="true" aria-label="Crop photo">
      <button className="secret-modal__scrim" onClick={onCancel} disabled={busy} />
      <div className="avatar-crop">
        <h2>Frame the photo</h2>
        <p>Drag to choose the square that will appear on BCKT. Motion is kept as an animated WebP.</p>
        <div
          className="avatar-crop__stage"
          onPointerDown={(event) => {
            (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, y: event.clientY, ox: clamped.x, oy: clamped.y };
          }}
          onPointerMove={(event) => {
            if (!drag.current) return;
            setOffset({
              x: drag.current.ox + (event.clientX - drag.current.x),
              y: drag.current.oy + (event.clientY - drag.current.y),
            });
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
        >
          {ready ? (
            <img
              src={preview}
              alt=""
              draggable={false}
              style={{
                width: drawWidth,
                height: drawHeight,
                transform: `translate(${clamped.x}px, ${clamped.y}px)`,
              }}
            />
          ) : null}
        </div>
        <div className="dialog-actions">
          <button className="button button--dash-quiet" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="button button--dash-primary"
            type="button"
            disabled={!ready || busy}
            data-state={busy ? 'loading' : undefined}
            onClick={() => onConfirm(crop())}
          >
            {busy ? 'Saving...' : 'Save photo'}
          </button>
        </div>
      </div>
    </div>
  );
}
